/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useContext } from 'react';
import { Price } from '@zapier/currency';

import { Interval, PlanV2 } from '@zapier/design-system-revenue/src/types';
import { getEnticements } from '@zapier/design-system-revenue/src/utils';
import { CurrencyContext } from '@zapier/design-system-revenue/src/contexts/CurrencyContext';

import { BillingOption } from './BillingOption';

type Props = {
  onSelectOption: (interval: Interval) => void;
  planOptions: PlanV2[];
  selectedOption: Interval;
};

const billingOptionsCss = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: 750px) {
    flex-direction: column;
  }
`;

export const BillingOptions = ({
  planOptions,
  onSelectOption,
  selectedOption,
}: Props) => {
  const currency = useContext(CurrencyContext);
  const enticements = getEnticements(planOptions);

  const billingOptions = planOptions.map(plan => {
    const intervalName = plan.interval === 'year' ? 'annual' : plan.interval;
    const name = `pay ${intervalName}ly`;

    // for the free plan monthly is the the only option, so always select it:
    const isSelected =
      planOptions.length === 1 || selectedOption === plan.interval;

    const price = plan.interval === 'year' ? plan.amount / 12 : plan.amount;

    return (
      <BillingOption
        enticement={enticements[plan.interval]}
        isMetered={plan.hasMeteredBilling}
        isOnly={planOptions.length === 1}
        isSelected={isSelected}
        key={name}
        name={name}
        onClick={() => onSelectOption(plan.interval)}
        price={<Price currency={currency}>{price}</Price>}
      />
    );
  });

  return (
    <div
      aria-label="billing interval options"
      css={billingOptionsCss}
      role="radiogroup"
    >
      {billingOptions}
    </div>
  );
};
